import React from 'react';
import * as d3 from 'd3';
import { NodeGroup, Animate } from 'react-move';
import { Context } from './Context';
import {
  easeExpInOut,
  easeCubicInOut,
  easeCubicOut,
  easeCubicIn,
  easeBounceInOut,
  easeBounceIn,
  easeBounceOut,
  easeElasticOut,
  easeBackOut
} from 'd3-ease';
import RadialTidyTreeNode from './Components/RadialTidyTreeNode';

class Component extends React.Component {
  state = {
    bundleChanged: !!this.props.initialize,
    zoomIndex: -1,
    active: this.context.data.results.map((d, i) => {
      return {
        id: d.id,
        prev: i !== 0 ? this.context.data.results[i - 1].id : undefined,
        next:
          i !== this.context.data.results.length - 1
            ? this.context.data.results[i + 1].id
            : undefined
      };
    })
  };

  componentWillReceiveProps(nextProps) {
    if (this.props.bundleKey !== nextProps.bundleKey) {
      this.setState({ bundleChanged: true });
    }
    if (this.props.anchors !== nextProps.anchors) {
      this.setState({ bundleChanged: false });
    }
  }

  componentDidMount() {
    // setTimeout(() => {
    //   this.setState({ bundleKey: 'state' });
    // }, 2000);
  }

  rotationalMargin = Math.PI / 40;

  maxRadius = (index) => {
    const { zoomIndex } = this.state;
    return (
      this.context.w /
      (zoomIndex !== -1
        ? zoomIndex === index
          ? 4
          : this.state.active.length * 2
        : Math.max(4, this.state.active.length * 2))
    );
  };

  zoomIn = (i) => {
    if (this.state.zoomIndex === i) {
      this.props.handleHover({});
    }
    this.setState({ zoomIndex: this.state.zoomIndex === i ? -1 : i });
  };

  get scale() {
    const { maxRadius } = this;
    const { zoomIndex } = this.state;
    const { w } = this.context;
    return {
      x: d3
        .scaleLinear()
        .domain(
          zoomIndex !== -1
            ? [zoomIndex - 0.5, zoomIndex + 0.5]
            : [0, Math.max(1, this.state.active.length - 1)]
        )
        .range([-w / 2 + maxRadius(zoomIndex), w / 2 - maxRadius(zoomIndex)])
    };
  }

  handleSelect = (leaf, id, index) => {
    const nextId = leaf.data.id;

    const active = [
      ...this.state.active,
      {
        id: nextId,
        prev: id
      }
    ];

    active[index].next = nextId;

    this.setState({
      active
    });
  };

  line = d3
    .line()
    .x((d) => {
      return d[0];
    })
    .y((d) => {
      return d[1];
    })
    .curve(d3.curveLinear);

  render() {
    const {
      w,
      h,
      margin: { top, left, bottom, right }
    } = this.context;

    const {
      scale: { x }
    } = this;

    const { rotationalMargin, maxRadius } = this;
    const { active, zoomIndex, bundleChanged } = this.state;
    const { anchors, bundleKey, enabled } = this.props;
    return (
      <g>
        <NodeGroup
          key={w + bundleKey + anchors + bundleChanged}
          data={
            !enabled
              ? []
              : !anchors.length
              ? anchors
              : active.map((d) => {
                  return {
                    ...this.context.data.results.find((dd) => dd.id === d.id),
                    treeNode: d
                  };
                })
          }
          keyAccessor={(d, i) => i + bundleKey}
          start={(d, i) => ({
            xPos: bundleChanged ? x(i) : anchors[i][0],
            yPos: bundleChanged ? 0 : anchors[i][1],
            xMid: x(i + 0.5),
            radians: 0,
            radiusMultiplier: 0.0,
            initialScaleMultiplier: bundleChanged ? 1 : 0,
            rotationMargin: 0,
            maxRad: maxRadius(i)
          })}
          leave={(d, i) => ({
            xPos: [anchors[i][0]],
            yPos: [anchors[i][1]],
            initialScaleMultiplier: [0],
            xMid: [x(i + 0.5)],
            radians: [0],
            radiusMultiplier: [0],
            rotationMargin: [0],
            maxRad: maxRadius(i),
            timing: {
              duration: 800,
              // delay: 1000,
              ease: easeCubicOut
            },
            events: {
              end: () => {
                // console.log('ended');
              }
            }
          })}
          enter={(d, i) => ({
            xPos: [x(i)],
            xMid: [x(i + 0.5)],
            yPos: [0],
            radians: [Math.PI],
            radiusMultiplier: [1],
            initialScaleMultiplier: [1],
            rotationMargin: [rotationalMargin],
            maxRad: [maxRadius(i)],
            timing: {
              duration: 800,
              // delay: 100 * i,
              ease: easeCubicInOut
            }
          })}
          update={(d, i) => ({
            xPos: [x(i)],
            yPos: [0],
            xMid: [x(i + 0.5)],
            radians: [Math.PI],
            radiusMultiplier: [1],
            initialScaleMultiplier: [1],
            maxRad: [maxRadius(i)],
            rotationMargin: [rotationalMargin],
            timing: {
              duration: 800,
              ease: easeCubicInOut
            }
          })}
        >
          {(nodes) => {
            if (!nodes.length) return <g />;
            return (
              <g>
                <path
                  transform={`translate(${(left + w / 2) *
                    nodes[0].state.initialScaleMultiplier}, ${(top + h / 2) *
                    nodes[0].state.initialScaleMultiplier})`}
                  d={this.line(nodes.map((d) => [d.state.xPos, d.state.yPos]))}
                  style={{
                    stroke: 'black',
                    strokeWidth: 2,
                    opacity: 1 - nodes[0].state.initialScaleMultiplier
                  }}
                />
                {nodes.map((node, j) => {
                  const name = node.data.committee_name;
                  return (
                    <g key={j} transform={`translate(0,${node.state.yPos})`}>
                      <g
                        transform={`translate(${(left + w / 2) *
                          node.state.initialScaleMultiplier}, ${(top + h / 2) *
                          node.state.initialScaleMultiplier})`}
                      >
                        <RadialTidyTreeNode
                          node={node}
                          index={j}
                          bundleKey={bundleKey}
                          handleSelect={this.handleSelect}
                          next={null}
                          zoomed={zoomIndex === j}
                          handleHover={this.props.handleHover}
                        />
                        <text
                          x={node.state.xPos}
                          y={30}
                          textAnchor='middle'
                          style={{
                            stroke: 'rgb(255, 255, 255)',
                            strokeWidth: 3
                          }}
                        >
                          {name.indexOf('ZIMMERMAN') !== -1 ? 'DAYPAC' : name}
                        </text>
                        <text x={node.state.xPos} y={30} textAnchor='middle'>
                          {name.indexOf('ZIMMERMAN') !== -1 ? 'DAYPAC' : name}
                        </text>
                        {zoomIndex === j && (
                          <g
                            transform={`translate(${node.state.xPos},${0})`}
                            className='in-out'
                          >
                            <text
                              transform={''}
                              dy={2}
                              dx={-20 * node.state.radiusMultiplier}
                              style={{
                                fontSize: '6px',
                                stroke: 'white',
                                strokeWidth: 3,
                                opacity: 1 * node.state.radiusMultiplier
                              }}
                              textAnchor='end'
                            >
                              INCOMING
                            </text>
                            <text
                              transform={''}
                              dy={2}
                              dx={-20 * node.state.radiusMultiplier}
                              style={{
                                fontSize: '6px',

                                opacity: 1 * node.state.radiusMultiplier
                              }}
                              textAnchor='end'
                            >
                              INCOMING
                            </text>
                            <text
                              transform={''}
                              dy={2}
                              dx={20 * node.state.radiusMultiplier}
                              style={{
                                fontSize: '6px',
                                stroke: 'white',
                                strokeWidth: 3,
                                opacity: 1 * node.state.radiusMultiplier
                              }}
                              textAnchor='start'
                            >
                              OUTGOING
                            </text>
                            <text
                              transform={''}
                              dy={2}
                              dx={20 * node.state.radiusMultiplier}
                              style={{
                                fontSize: '6px',
                                opacity: 1 * node.state.radiusMultiplier
                              }}
                              textAnchor='start'
                            >
                              OUTGOING
                            </text>
                          </g>
                        )}
                        <circle
                          cx={node.state.xPos}
                          cy={0}
                          r={10}
                          style={{ fill: '#3d8feb' }}
                          onClick={() => this.zoomIn(j)}
                          className='zoom-circle'
                        />
                      </g>
                    </g>
                  );
                })}
              </g>
            );
          }}
        </NodeGroup>
      </g>
    );
  }
}

Component.contextType = Context;

export default Component;
